import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { HiChevronDown, HiChevronUp, HiDotsVertical } from 'react-icons/hi';
import { HSpace, VSpace } from '@/components/spacing';
import { Container } from '@/features/details/sessions-type-chip';
import { EditorContext } from '@/features/editor/context/editor-context';
import { countryCodeToFlag, countryCodes } from '@/utils/country-codes';

export function PreviewActions() {
  const {
    devicePreview: { addCartItem, removeAllCartItems, changeLocale },
    deviceNavigation: { previewUrl },
  } = useContext(EditorContext);
  const [open, setOpen] = useState(false);
  const [marketOpen, setMarketOpen] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState<string | null>(null);
  const onAddCartItem = () => {
    addCartItem();
  };

  const onRemoveAllCartItems = () => {
    removeAllCartItems();
  };

  const onMarketChange = (market: string) => {
    setSelectedMarket(market);
    changeLocale(market, previewUrl);
    setMarketOpen(false);
  };

  return (
    <Popover
      onClickOutside={() => setOpen(!open)}
      isOpen={open}
      containerStyle={{ zIndex: `29` }}
      positions={[`bottom`, `left`, `right`]}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={20}
        >
          <PopoverContainer>
            <Header>SIMULATOR ACTIONS</Header>
            <Divider />
            <ActionItem onClick={() => onAddCartItem()}>
              Add a Cart Item
            </ActionItem>
            <Divider />
            <ActionItem onClick={() => onRemoveAllCartItems()}>
              Remove all Cart Items
            </ActionItem>
            <Divider />
            <VSpace value={0.5} />
            <Header>MARKET</Header>

            <MarketSelect onClick={() => setMarketOpen(!marketOpen)}>
              <span>
                {selectedMarket
                  ? `${countryCodeToFlag(selectedMarket)} ${
                      countryCodes.find((c) => c.key === selectedMarket)?.name
                    }`
                  : `Choose Market`}
              </span>
              {marketOpen ? <HiChevronUp /> : <HiChevronDown />}
            </MarketSelect>

            {marketOpen && (
              <MarketList>
                {countryCodes.map((c) => (
                  <MarketItem key={c.key} onClick={() => onMarketChange(c.key)}>
                    {countryCodeToFlag(c.key)} {c.name}
                  </MarketItem>
                ))}
              </MarketList>
            )}
          </PopoverContainer>
        </ArrowContainer>
      )}
    >
      <Container>
        <HSpace />
        <ChipWrap onClick={() => setOpen(!open)}>
          <HiDotsVertical />
        </ChipWrap>
      </Container>
    </Popover>
  );
}

const PopoverContainer = styled.div`
  user-select: none;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background-color: white;
  padding: 1.6rem;
  max-width: 300px;
  width: 300px;
  border: solid 1px rgba(151, 151, 151, 0.1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);
`;

const ChipWrap = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;

  cursor: pointer;
  background: rgba(52, 69, 134, 0.06);

  font-size: 1.2rem;
  font-weight: 500;
  font-family: 'JetBrains Mono', serif;
  user-select: none;

  color: #545454;
  border-radius: 2rem;
  padding: 1rem;

  box-shadow: ${(props: any) =>
    props.whiteBg
      ? `rgba(0, 0, 0, 0.08) 0px 1px 6px`
      : `0 1px 4px 0 rgba(68, 72, 80, 0.08)`};

  :active {
    opacity: 1;
  }
`;

const Header = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #9f9f9f;
  margin: 0.2rem 0rem 0.6rem 0.3rem;
`;

const ActionItem = styled.div`
  font-size: 1.3rem;
  font-weight: 500;
  color: #000;
  padding: 1rem 0;
  cursor: pointer;
  white-space: nowrap;
  margin-left: 0.3rem;

  &:hover {
    opacity: 0.7;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #dcdcdc;
  opacity: 0.8;
  margin: 0.5rem 0 0.5rem 0;
`;

const MarketSelect = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  font-size: 1.25rem;
  font-weight: 500;
  padding: 0.9rem 1rem;
  margin-top: 0.4rem;

  background: rgba(52, 69, 134, 0.06);
  border-radius: 8px;
  cursor: pointer;
  color: #000;

  svg {
    font-size: 1.2rem;
    opacity: 0.6;
  }

  &:hover {
    background: rgba(52, 69, 134, 0.12);
  }
`;

const MarketList = styled.div`
  margin-top: 0.6rem;
  max-height: 180px;
  overflow-y: auto;

  border-radius: 8px;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.08);
`;

const MarketItem = styled.div`
  padding: 0.8rem 0.5rem;
  font-size: 1.25rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #f3f3f3;
  }
`;
